document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function createDrawerController(options) {
  const root = document.querySelector(options.root);
  const panel = root?.querySelector(options.panel);
  const openButtons = document.querySelectorAll(options.open);
  const closeButtons = root?.querySelectorAll(options.close) || [];

  if (!root || !panel || openButtons.length === 0) return null;

  let lastActiveElement = null;

  const setOpenState = (isOpen) => {
    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', String(isOpen));
    });
  };

  const focusFirstElement = () => {
    const focusable = panel.querySelectorAll(focusableSelector);
    const target = focusable[0] || panel;
    target.focus({ preventScroll: true });
  };

  const open = () => {
    lastActiveElement = document.activeElement;
    root.classList.remove('hidden');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    setOpenState(true);
    options.onOpen?.();

    if (prefersReducedMotion.matches) {
      panel.style.transitionDuration = '0ms';
      panel.classList.remove(options.closedTransform);
      focusFirstElement();
      return;
    }

    requestAnimationFrame(() => {
      panel.classList.remove(options.closedTransform);
      focusFirstElement();
    });
  };

  const close = () => {
    panel.classList.add(options.closedTransform);
    root.setAttribute('aria-hidden', 'true');
    setOpenState(false);

    window.setTimeout(() => {
      root.classList.add('hidden');
      if (!document.querySelector('[data-mobile-menu]:not(.hidden), [data-cart-drawer]:not(.hidden)')) {
        document.body.classList.remove('overflow-hidden');
      }
      if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
        lastActiveElement.focus({ preventScroll: true });
      }
    }, prefersReducedMotion.matches ? 0 : 300);
  };

  openButtons.forEach((button) => button.addEventListener('click', open));
  closeButtons.forEach((button) => button.addEventListener('click', close));

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(panel.querySelectorAll(focusableSelector));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  return { open, close };
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  })[character]);
}

function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents);
  }

  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency,
  }).format((Number(cents) || 0) / 100);
}

function getCartRoutes() {
  return {
    cart: `${window.Shopify?.routes?.root || '/'}cart`,
    add: `${window.Shopify?.routes?.root || '/'}cart/add.js`,
    change: `${window.Shopify?.routes?.root || '/'}cart/change.js`,
    get: `${window.Shopify?.routes?.root || '/'}cart.js`,
    products: `${window.Shopify?.routes?.root || '/'}collections/all`,
  };
}

function createCartDrawer() {
  const root = document.querySelector('[data-cart-drawer]');
  if (!root) return null;

  const itemsTarget = root.querySelector('[data-cart-items]');
  const footerTarget = root.querySelector('[data-cart-footer]');
  const loading = root.querySelector('[data-cart-loading]');
  const counts = document.querySelectorAll('[data-cart-count]');
  const routes = getCartRoutes();
  let lastRequest = Promise.resolve();

  const setLoading = (isLoading) => {
    if (loading) loading.hidden = !isLoading;
    root.querySelectorAll('button, input').forEach((control) => {
      if (!control.matches('[data-cart-drawer-close]')) control.disabled = isLoading;
    });
  };

  const setCounts = (count) => {
    counts.forEach((target) => {
      target.textContent = String(count);
    });
  };

  const renderError = (message, retry) => {
    if (!itemsTarget) return;
    itemsTarget.innerHTML = `
      <div class="flex min-h-full flex-col items-center justify-center text-center">
        <p class="font-serif text-3xl italic text-brand-dark">Cart update failed.</p>
        <p class="mt-3 max-w-xs text-sm text-[rgb(var(--color-muted))]">${escapeHtml(message || 'Please try again or use the cart page.')}</p>
        <div class="mt-6 flex flex-col gap-3 sm:flex-row">
          <button type="button" class="button" data-cart-retry>Try Again</button>
          <a class="button button--secondary" href="${routes.cart}">View Cart</a>
        </div>
      </div>
    `;
    itemsTarget.querySelector('[data-cart-retry]')?.addEventListener('click', retry || refresh);
  };

  const renderItems = (cart) => {
    if (!itemsTarget) return;

    if (!cart.items.length) {
      itemsTarget.innerHTML = `
        <div class="flex min-h-full flex-col items-center justify-center text-center">
          <div class="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-brand-mint text-brand-green">
            <svg class="h-8 w-8" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 7h15l-1.5 9h-12z"/><path d="M6 7 5.2 3H2"/><circle cx="9" cy="20" r="1"/><circle cx="18" cy="20" r="1"/>
            </svg>
          </div>
          <p class="font-serif text-3xl italic text-brand-dark">Your cart is empty.</p>
          <p class="mt-3 max-w-xs text-sm text-[rgb(var(--color-muted))]">Start with a DIY potting kit, succulent, or event-ready add-on.</p>
        </div>
      `;
      return;
    }

    itemsTarget.innerHTML = `
      <div class="grid gap-4">
        ${cart.items.map((item, index) => {
          const line = index + 1;
          const image = item.image
            ? `<img class="h-full w-full object-cover" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
            : '';
          const variant = item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="mt-1 truncate text-xs text-[rgb(var(--color-muted))]">${escapeHtml(item.variant_title)}</p>`
            : '';

          return `
            <article class="relative flex gap-4 rounded-[1.5rem] border border-brand-border bg-white p-4 shadow-card" data-cart-line="${line}">
              <button type="button" class="absolute -right-2 -top-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-brand-border bg-white text-gray-400 shadow-card transition hover:border-brand-purple hover:text-brand-purple" data-cart-line-remove aria-label="Remove ${escapeHtml(item.product_title)}">
                <svg class="h-3 w-3" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
              <a class="h-20 w-20 shrink-0 overflow-hidden rounded-media bg-brand-bg" href="${escapeHtml(item.url)}">${image}</a>
              <div class="min-w-0 flex-1">
                <a class="block truncate font-serif text-sm text-brand-dark" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
                ${variant}
                <div class="mt-3 flex items-center justify-between gap-3">
                  <div class="inline-flex h-8 w-24 items-center overflow-hidden rounded-full border border-brand-border bg-white shadow-sm" data-quantity-control>
                    <button class="flex h-full w-8 shrink-0 items-center justify-center text-gray-500 transition-colors hover:bg-brand-bg hover:text-brand-dark" type="button" data-quantity-decrement aria-label="Decrease quantity">
                      <svg class="h-3 w-3" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                    </button>
                    <label class="sr-only" for="CartDrawerQuantity-${line}">Quantity</label>
                    <input id="CartDrawerQuantity-${line}" class="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-center text-sm font-medium text-brand-dark focus-visible:shadow-none" type="number" name="updates[]" value="${item.quantity}" min="0" inputmode="numeric">
                    <button class="flex h-full w-8 shrink-0 items-center justify-center text-gray-500 transition-colors hover:bg-brand-bg hover:text-brand-dark" type="button" data-quantity-increment aria-label="Increase quantity">
                      <svg class="h-3 w-3" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                  <span class="text-sm font-medium text-brand-dark">${formatMoney(item.final_line_price)}</span>
                </div>
              </div>
            </article>
          `;
        }).join('')}
      </div>
    `;
  };

  const renderFooter = (cart) => {
    if (!footerTarget) return;
    footerTarget.innerHTML = `
      <div class="mb-4 flex items-center justify-between text-sm">
        <span class="text-[rgb(var(--color-muted))]">Subtotal</span>
        <span class="font-medium text-brand-dark">${formatMoney(cart.total_price)}</span>
      </div>
      ${cart.item_count > 0
        ? `<form action="${routes.cart}" method="post"><button class="button w-full" type="submit" name="checkout">Checkout</button></form>`
        : `<a class="button w-full" href="${routes.products}">Shop Kits</a>`}
      <a class="mt-3 block text-center text-xs underline underline-offset-4 transition hover:text-brand-green" href="${routes.cart}">View cart</a>
    `;
  };

  const renderCart = (cart) => {
    setCounts(cart.item_count);
    renderItems(cart);
    renderFooter(cart);
  };

  const requestCart = (url, options) => {
    lastRequest = lastRequest.catch(() => {}).then(async () => {
      setLoading(true);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(options?.headers || {}) },
        ...options,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'The cart could not be updated.');
      renderCart(data);
      return data;
    }).catch((error) => {
      renderError(error.message, refresh);
      return null;
    }).finally(() => {
      setLoading(false);
    });

    return lastRequest;
  };

  const refresh = () => requestCart(routes.get);

  const changeLine = (line, quantity) => requestCart(routes.change, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, quantity }),
  });

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-line] input[type="number"]');
    if (!input) return;
    const line = Number(input.closest('[data-cart-line]').dataset.cartLine);
    const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
    changeLine(line, quantity);
  });

  root.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-line-remove]');
    if (!remove) return;
    const line = Number(remove.closest('[data-cart-line]').dataset.cartLine);
    changeLine(line, 0);
  });

  return { refresh, renderCart };
}

function initQuantityControls() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-decrement], [data-quantity-increment]');
    if (!button) return;

    const control = button.closest('[data-quantity-control]');
    const input = control?.querySelector('input[type="number"]');
    if (!input) return;

    const change = button.matches('[data-quantity-increment]') ? 1 : -1;
    const currentValue = Number.parseInt(input.value, 10) || 0;
    const min = Number.parseInt(input.min, 10);
    const max = input.max ? Number.parseInt(input.max, 10) : null;
    let nextValue = currentValue + change;

    if (!Number.isNaN(min)) nextValue = Math.max(min, nextValue);
    if (max !== null) nextValue = Math.min(max, nextValue);

    input.value = String(nextValue);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function initProductForms(cartDrawer) {
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || !cartDrawer) return;

    event.preventDefault();
    const submitter = event.submitter;
    submitter?.setAttribute('aria-busy', 'true');
    submitter && (submitter.disabled = true);

    try {
      const response = await fetch(getCartRoutes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Item could not be added.');
      await cartDrawer.refresh();
      cartDrawer.open?.();
    } catch (error) {
      form.submit();
    } finally {
      submitter?.removeAttribute('aria-busy');
      submitter && (submitter.disabled = false);
    }
  });
}

function initAccordions() {
  document.querySelectorAll('details').forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', String(details.open));
    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', String(details.open));
    });
  });
}

initQuantityControls();
initAccordions();

createDrawerController({
  root: '[data-mobile-menu]',
  panel: '[data-mobile-menu-panel]',
  open: '[data-mobile-menu-open]',
  close: '[data-mobile-menu-close]',
  closedTransform: 'translate-x-[-100%]',
});

const cartDrawer = createCartDrawer();
const cartDrawerController = createDrawerController({
  root: '[data-cart-drawer]',
  panel: '[data-cart-drawer-panel]',
  open: '[data-cart-drawer-open]',
  close: '[data-cart-drawer-close]',
  closedTransform: 'translate-x-full',
  onOpen: cartDrawer?.refresh,
});

if (cartDrawer && cartDrawerController) {
  cartDrawer.open = cartDrawerController.open;
}

initProductForms(cartDrawer);
